import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { ClipboardCheck, Clock, CalendarDays, User, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';

export default function RegularizationPage() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => { fetchRequests(); }, []);

  async function fetchRequests() {
    setLoading(true);
    try {
      const res = await api.get('/hr/regularization-requests');
      setRequests(res.data || []);
    } catch {
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }

  const filtered = statusFilter === 'all' ? requests : requests.filter(r => r.status === statusFilter);

  const statusColor = {
    pending: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20',
    approved: 'bg-green-500/10 text-green-600 border-green-500/20',
    rejected: 'bg-red-500/10 text-red-600 border-red-500/20',
    pending_manager: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20',
    pending_hr: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
    pending_ceo: 'bg-purple-500/10 text-purple-600 border-purple-500/20',
  };

  const statusLabel = (s) => {
    const labels = { pending: 'Pending', pending_manager: 'Pending Manager', pending_hr: 'Pending HR', pending_ceo: 'Pending CEO', approved: 'Approved', rejected: 'Rejected' };
    return labels[s] || s;
  };

  return (
    <div className="space-y-6" data-testid="regularization-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Applied Regularization</h1>
          <p className="text-muted-foreground text-sm mt-1">View all attendance regularization requests</p>
        </div>
      </div>

      <div className="flex gap-3 items-center">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]" data-testid="status-filter">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Requests</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
        <Badge variant="outline">{filtered.length} request(s)</Badge>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <ClipboardCheck className="h-12 w-12 mx-auto text-muted-foreground/40 mb-4" />
            <h3 className="text-lg font-semibold mb-1">No Regularization Requests</h3>
            <p className="text-muted-foreground text-sm">No requests found for the selected filter.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((req, i) => (
            <Card key={req.id || i} data-testid={`regularization-card-${i}`}>
              <CardContent className="py-4 flex items-center gap-4">
                <div className="flex-shrink-0">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="h-5 w-5 text-primary" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium">{req.employee_name || 'Employee'}</p>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                    <span className="flex items-center gap-1"><CalendarDays className="h-3 w-3" />{req.date || 'N/A'}</span>
                    <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{req.corrected_in || req.original_in || '--:--'} - {req.corrected_out || req.original_out || '--:--'}</span>
                    {req.type && <Badge variant="outline" className="text-xs">{req.type}</Badge>}
                  </div>
                  {req.reason && <p className="text-xs text-muted-foreground mt-1 truncate">{req.reason}</p>}
                </div>
                <Badge className={statusColor[req.status] || 'bg-muted'}>
                  {req.status === 'approved' && <CheckCircle className="h-3 w-3 mr-1" />}
                  {req.status === 'rejected' && <XCircle className="h-3 w-3 mr-1" />}
                  {req.status?.startsWith('pending') && <AlertTriangle className="h-3 w-3 mr-1" />}
                  {statusLabel(req.status)}
                </Badge>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
